import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  RocketLaunch, Clock, MapPin, Timer, Buildings,
  CheckCircle, ArrowRight, ArrowLeft, Confetti, X
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'tat_config', title: 'TAT & Cutoff', desc: 'Set order processing and dispatch turnaround times', icon: Clock, color: '#7d64ff' },
  { key: 'pincodes', title: 'Pincode Coverage', desc: 'Add the pincodes your business can service', icon: MapPin, color: '#28cd8c' },
  { key: 'delivery_slots', title: 'Delivery Slots', desc: 'Configure delivery time windows for customers', icon: Timer, color: '#f6a609' },
  { key: 'store_setup', title: 'Store Setup', desc: 'Create your first store or warehouse location', icon: Buildings, color: '#ea4b5a' },
];

function TatStep({ onComplete }) {
  const [config, setConfig] = useState({ processing_tat_hours: 24, dispatch_tat_hours: 48, cutoff_time: '18:00', cpt_hours: 72 });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/seller/tat-config`, config, { withCredentials: true });
      toast.success('TAT configuration saved');
      onComplete();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="wizard-step-tat">
      <p className="text-sm text-[#6b6b8a]">Define how quickly you process and dispatch orders. These timelines set customer expectations.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-[#f9f9fb] rounded-xl p-4">
          <Label className="text-xs font-semibold text-[#6b6b8a]">Processing TAT</Label>
          <p className="text-[10px] text-[#9c9db7] mb-2">Time to pick & pack after order accepted</p>
          <div className="flex items-center gap-2">
            <Input type="number" value={config.processing_tat_hours} onChange={e => setConfig(c => ({ ...c, processing_tat_hours: parseInt(e.target.value) || 24 }))} className="w-24" data-testid="wizard-processing-tat" />
            <span className="text-sm text-[#6b6b8a]">hours</span>
          </div>
        </div>
        <div className="bg-[#f9f9fb] rounded-xl p-4">
          <Label className="text-xs font-semibold text-[#6b6b8a]">Dispatch TAT</Label>
          <p className="text-[10px] text-[#9c9db7] mb-2">Max time to hand over to courier</p>
          <div className="flex items-center gap-2">
            <Input type="number" value={config.dispatch_tat_hours} onChange={e => setConfig(c => ({ ...c, dispatch_tat_hours: parseInt(e.target.value) || 48 }))} className="w-24" data-testid="wizard-dispatch-tat" />
            <span className="text-sm text-[#6b6b8a]">hours</span>
          </div>
        </div>
        <div className="bg-[#f9f9fb] rounded-xl p-4">
          <Label className="text-xs font-semibold text-[#6b6b8a]">Daily Cutoff Time</Label>
          <p className="text-[10px] text-[#9c9db7] mb-2">Orders after this → next business day</p>
          <Input type="time" value={config.cutoff_time} onChange={e => setConfig(c => ({ ...c, cutoff_time: e.target.value }))} className="w-36" data-testid="wizard-cutoff" />
        </div>
        <div className="bg-[#f9f9fb] rounded-xl p-4">
          <Label className="text-xs font-semibold text-[#6b6b8a]">Customer Promise Time</Label>
          <p className="text-[10px] text-[#9c9db7] mb-2">Total delivery promise from order placement</p>
          <div className="flex items-center gap-2">
            <Input type="number" value={config.cpt_hours} onChange={e => setConfig(c => ({ ...c, cpt_hours: parseInt(e.target.value) || 72 }))} className="w-24" data-testid="wizard-cpt" />
            <span className="text-sm text-[#6b6b8a]">hours</span>
          </div>
        </div>
      </div>
      <Button onClick={handleSave} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="wizard-save-tat">
        {saving ? 'Saving...' : 'Save & Continue'}
      </Button>
    </div>
  );
}

function PincodeStep({ onComplete }) {
  const [pincodes, setPincodes] = useState([{ pincode: '', city: '', state: '', delivery_days: 3, cod_available: true }]);
  const [saving, setSaving] = useState(false);

  const addRow = () => setPincodes(p => [...p, { pincode: '', city: '', state: '', delivery_days: 3, cod_available: true }]);
  const updateRow = (i, field, val) => setPincodes(p => p.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const removeRow = (i) => setPincodes(p => p.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    const valid = pincodes.filter(p => p.pincode.trim());
    if (valid.length === 0) { toast.error('Add at least one pincode'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/seller/pincodes`, valid, { withCredentials: true });
      toast.success(`${valid.length} pincodes added`);
      onComplete();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="wizard-step-pincodes">
      <p className="text-sm text-[#6b6b8a]">Add pincodes your business can deliver to. You can add more later from the Pincode Matrix page.</p>
      <div className="space-y-3">
        {pincodes.map((p, i) => (
          <div key={i} className="flex gap-2 items-end bg-[#f9f9fb] rounded-xl p-3">
            <div className="flex-1">
              <Label className="text-[10px]">Pincode</Label>
              <Input value={p.pincode} onChange={e => updateRow(i, 'pincode', e.target.value)} placeholder="110001" className="h-9" data-testid={`wizard-pincode-${i}`} />
            </div>
            <div className="flex-1">
              <Label className="text-[10px]">City</Label>
              <Input value={p.city} onChange={e => updateRow(i, 'city', e.target.value)} placeholder="New Delhi" className="h-9" />
            </div>
            <div className="w-16">
              <Label className="text-[10px]">Days</Label>
              <Input type="number" value={p.delivery_days} onChange={e => updateRow(i, 'delivery_days', parseInt(e.target.value) || 3)} className="h-9" />
            </div>
            {pincodes.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => removeRow(i)} className="text-red-400 h-9"><X size={14} /></Button>
            )}
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addRow} className="text-[#7d64ff] border-dashed" data-testid="wizard-add-pincode">+ Add Another Pincode</Button>
      <div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="wizard-save-pincodes">
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </div>
    </div>
  );
}

function SlotStep({ onComplete }) {
  const [slots, setSlots] = useState([
    { slot_name: 'Morning', start_time: '09:00', end_time: '12:00', max_orders: 50 },
    { slot_name: 'Afternoon', start_time: '12:00', end_time: '17:00', max_orders: 50 },
  ]);
  const [saving, setSaving] = useState(false);

  const updateSlot = (i, field, val) => setSlots(s => s.map((sl, idx) => idx === i ? { ...sl, [field]: val } : sl));

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const slot of slots.filter(s => s.slot_name)) {
        await axios.post(`${API}/api/seller/slots`, { ...slot, is_active: true, days: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'] }, { withCredentials: true });
      }
      toast.success('Delivery slots configured');
      onComplete();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="wizard-step-slots">
      <p className="text-sm text-[#6b6b8a]">Set up delivery time windows. Customers will choose from these slots at checkout.</p>
      <div className="space-y-3">
        {slots.map((s, i) => (
          <div key={i} className="bg-[#f9f9fb] rounded-xl p-4">
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-[10px]">Slot Name</Label>
                <Input value={s.slot_name} onChange={e => updateSlot(i, 'slot_name', e.target.value)} className="h-9" data-testid={`wizard-slot-name-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Start</Label>
                <Input type="time" value={s.start_time} onChange={e => updateSlot(i, 'start_time', e.target.value)} className="h-9" />
              </div>
              <div>
                <Label className="text-[10px]">End</Label>
                <Input type="time" value={s.end_time} onChange={e => updateSlot(i, 'end_time', e.target.value)} className="h-9" />
              </div>
              <div>
                <Label className="text-[10px]">Max Orders</Label>
                <Input type="number" value={s.max_orders} onChange={e => updateSlot(i, 'max_orders', parseInt(e.target.value) || 50)} className="h-9" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button onClick={handleSave} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="wizard-save-slots">
        {saving ? 'Saving...' : 'Save & Continue'}
      </Button>
    </div>
  );
}

function StoreStep({ onComplete }) {
  const [form, setForm] = useState({ store_name: '', store_code: '', address: '', city: '', pincode: '', is_return_center: false, tat_hours: 24 });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.store_name || !form.store_code) { toast.error('Store name and code required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/seller/stores`, form, { withCredentials: true });
      toast.success('Store created');
      onComplete();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="wizard-step-store">
      <p className="text-sm text-[#6b6b8a]">Set up your first fulfillment location. You can add more stores later from the Store Network page.</p>
      <div className="bg-[#f9f9fb] rounded-xl p-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Store Name</Label>
            <Input value={form.store_name} onChange={e => setForm(f => ({ ...f, store_name: e.target.value }))} placeholder="Main Warehouse" data-testid="wizard-store-name" />
          </div>
          <div>
            <Label className="text-xs">Store Code</Label>
            <Input value={form.store_code} onChange={e => setForm(f => ({ ...f, store_code: e.target.value }))} placeholder="WH-01" data-testid="wizard-store-code" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Address</Label>
          <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Full address" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">City</Label>
            <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Mumbai" />
          </div>
          <div>
            <Label className="text-xs">Pincode</Label>
            <Input value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} placeholder="400001" />
          </div>
          <div>
            <Label className="text-xs">Processing TAT (hrs)</Label>
            <Input type="number" value={form.tat_hours} onChange={e => setForm(f => ({ ...f, tat_hours: parseInt(e.target.value) || 24 }))} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.is_return_center} onChange={e => setForm(f => ({ ...f, is_return_center: e.target.checked }))} className="rounded" />
          Also use as return center
        </label>
      </div>
      <Button onClick={handleSave} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="wizard-save-store">
        {saving ? 'Saving...' : 'Complete Setup'}
      </Button>
    </div>
  );
}

function CompletionScreen() {
  const navigate = useNavigate();
  return (
    <div className="text-center py-12" data-testid="wizard-complete">
      <div className="w-20 h-20 rounded-full bg-[#28cd8c]/10 flex items-center justify-center mx-auto mb-6">
        <Confetti size={40} className="text-[#28cd8c]" />
      </div>
      <h2 className="text-2xl font-bold text-[#0A0A0A] mb-2">You're all set!</h2>
      <p className="text-[#6b6b8a] max-w-md mx-auto mb-8">
        Your seller account is configured and ready to receive orders. You can always fine-tune these settings from the Seller Config section.
      </p>
      <div className="flex gap-3 justify-center">
        <Button onClick={() => navigate('/dashboard')} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="wizard-go-dashboard">
          Go to Dashboard
        </Button>
        <Button variant="outline" onClick={() => navigate('/orders')} data-testid="wizard-go-orders">
          View Orders
        </Button>
      </div>
    </div>
  );
}

export default function GettingStartedPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [allDone, setAllDone] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/onboarding-status`, { withCredentials: true });
      setCompletedSteps(data.steps || {});
      if (data.completed) setAllDone(true);
      // Jump to first incomplete step
      const firstIncomplete = STEPS.findIndex(s => !(data.steps || {})[s.key]);
      if (firstIncomplete >= 0) setCurrentStep(firstIncomplete);
    } catch { /* defaults */ }
    finally { setLoading(false); }
  };

  const markStepComplete = useCallback(async (stepKey) => {
    try {
      const { data } = await axios.post(`${API}/api/seller/onboarding-step`, { step: stepKey, completed: true }, { withCredentials: true });
      setCompletedSteps(prev => ({ ...prev, [stepKey]: true }));
      if (data.all_complete) {
        setAllDone(true);
      } else {
        // Auto-advance to next step
        const nextIdx = STEPS.findIndex(s => s.key === stepKey) + 1;
        if (nextIdx < STEPS.length) setCurrentStep(nextIdx);
      }
    } catch { /* ignore */ }
  }, []);

  const completedCount = Object.values(completedSteps).filter(Boolean).length;
  const pct = Math.round((completedCount / STEPS.length) * 100);

  if (loading) return <DashboardLayout><div className="p-6 text-center text-[#9c9db7]">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl" data-testid="getting-started-page">
        {allDone ? <CompletionScreen /> : (
          <>
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <RocketLaunch size={28} className="text-[#7d64ff]" />
                <h1 className="text-2xl font-bold text-[#0A0A0A]">Getting Started</h1>
              </div>
              <p className="text-sm text-[#6b6b8a]">Complete these 4 steps to configure your seller account and start receiving orders.</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex-1 h-2 bg-[#e5e5ea] rounded-full overflow-hidden">
                  <div className="h-full bg-[#7d64ff] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} data-testid="wizard-progress-bar" />
                </div>
                <span className="text-sm font-semibold text-[#7d64ff]">{completedCount}/{STEPS.length}</span>
              </div>
            </div>

            {/* Step indicators */}
            <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
              {STEPS.map((step, i) => {
                const done = completedSteps[step.key];
                const active = i === currentStep;
                return (
                  <button
                    key={step.key}
                    onClick={() => setCurrentStep(i)}
                    className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all min-w-[200px] text-left ${
                      active ? 'border-[#7d64ff] bg-[#7d64ff]/5 shadow-sm' : done ? 'border-[#28cd8c]/40 bg-[#28cd8c]/5' : 'border-[#e5e5ea] hover:border-[#7d64ff]/30'
                    }`}
                    data-testid={`wizard-step-indicator-${step.key}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      done ? 'bg-[#28cd8c] text-white' : active ? 'bg-[#7d64ff] text-white' : 'bg-[#e5e5ea] text-[#9c9db7]'
                    }`}>
                      {done ? <CheckCircle size={16} weight="bold" /> : <span className="text-xs font-bold">{i + 1}</span>}
                    </div>
                    <div>
                      <p className={`text-xs font-semibold ${done ? 'text-[#28cd8c]' : active ? 'text-[#7d64ff]' : 'text-[#3c3c64]'}`}>{step.title}</p>
                      <p className="text-[10px] text-[#9c9db7]">{done ? 'Complete' : step.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Step content */}
            <div className="bg-white rounded-2xl border border-[#e5e5ea] p-6">
              <div className="flex items-center gap-3 mb-5">
                {(() => { const S = STEPS[currentStep]; const Icon = S.icon; return <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: S.color + '15' }}><Icon size={22} style={{ color: S.color }} /></div>; })()}
                <div>
                  <h2 className="text-lg font-bold text-[#0A0A0A]">{STEPS[currentStep].title}</h2>
                  <p className="text-xs text-[#9c9db7]">Step {currentStep + 1} of {STEPS.length}</p>
                </div>
              </div>

              {completedSteps[STEPS[currentStep].key] ? (
                <div className="flex items-center gap-3 py-8 justify-center text-[#28cd8c]">
                  <CheckCircle size={24} weight="fill" />
                  <span className="font-semibold">This step is already complete!</span>
                  {currentStep < STEPS.length - 1 && (
                    <Button variant="outline" size="sm" onClick={() => setCurrentStep(currentStep + 1)} className="ml-4" data-testid="wizard-next-step">
                      Next Step <ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {currentStep === 0 && <TatStep onComplete={() => markStepComplete('tat_config')} />}
                  {currentStep === 1 && <PincodeStep onComplete={() => markStepComplete('pincodes')} />}
                  {currentStep === 2 && <SlotStep onComplete={() => markStepComplete('delivery_slots')} />}
                  {currentStep === 3 && <StoreStep onComplete={() => markStepComplete('store_setup')} />}
                </>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-4">
              <Button variant="ghost" disabled={currentStep === 0} onClick={() => setCurrentStep(c => c - 1)} data-testid="wizard-prev">
                <ArrowLeft size={14} className="mr-1" /> Previous
              </Button>
              {currentStep < STEPS.length - 1 && (
                <Button variant="ghost" onClick={() => setCurrentStep(c => c + 1)} data-testid="wizard-skip">
                  Skip for now <ArrowRight size={14} className="ml-1" />
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
